import { useMemo, Dispatch, SetStateAction, JSX } from 'react';
import { Button } from '@Components/Button';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { Appearance, Size } from '@Components/designConstants';
import { abbreviateNumber, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { MdClose } from 'react-icons/md';
import cn from 'classnames';
import './PinnedSpendTable.scss';
import { FaChartPie } from 'react-icons/fa';
import {
  EfficientFrontierSeries,
  getPercentageDifferenceIndicator
} from '../utils/efficientFrontierUtils';
import { PinnedSpendPointData } from '../../hroas-types';

const renderNoRows = () => <div>No rows to show.</div>;

interface PinnedSpendPointsTableProps {
  // Optional: DEV-21410 suppresses BAU entirely (undefined) when the selected date range's BAU
  // spend falls below the lowest modeled budget level. Already accessed via optional chaining below.
  businessAsUsual: PinnedSpendPointData | undefined;
  setPinnedSpendPoints: Dispatch<SetStateAction<Map<number, PinnedSpendPointData>>>;
  pinnedSpendPointsData: PinnedSpendPointData[];
  onPieChartClick: (dataPoint: PinnedSpendPointData) => void;
  series: EfficientFrontierSeries[];
}

export const PinnedSpendPointsTable = ({
  businessAsUsual,
  setPinnedSpendPoints,
  pinnedSpendPointsData,
  onPieChartClick,
  series
}: PinnedSpendPointsTableProps): JSX.Element => {
  const tableHeaders = useMemo(() => {
    const baseHeaderProps = {
      flex: 1,
      minFlexWidth: 120
    };

    const headers: Header[] = [
      {
        ...baseHeaderProps,
        name: 'totalBudget',
        label: 'Spend',
        flex: 2,
        renderer: (row: PinnedSpendPointData) => {
          const isBau = row.totalBudget === businessAsUsual?.totalBudget;
          const { indicatorClassName, indicatorText } = getPercentageDifferenceIndicator(
            row.totalBudget || 0,
            businessAsUsual?.totalBudget || 0,
            isBau,
            true
          );
          return (
            <InteractiveDiv
              className={cn('ef-table-cell', 'ef-spend-cell', { 'business-as-usual': isBau })}
              onClick={() => onPieChartClick(row)}
            >
              <span>{`$${abbreviateNumber(row.totalBudget || null, 1)}`}</span>
              {indicatorText && <div className={indicatorClassName}>{indicatorText}</div>}
            </InteractiveDiv>
          );
        }
      },
      {
        ...baseHeaderProps,
        name: 'totalHolisticRevenue',
        label: 'Revenue',
        flex: 2,
        renderer: (row: PinnedSpendPointData) => {
          const isBau = row.totalBudget === businessAsUsual?.totalBudget;
          const { indicatorClassName, indicatorText } = getPercentageDifferenceIndicator(
            row.totalHolisticRevenue || 0,
            businessAsUsual?.totalHolisticRevenue || 0,
            isBau
          );
          return (
            <InteractiveDiv
              className={cn('ef-table-cell', 'ef-revenue-cell', { 'business-as-usual': isBau })}
              onClick={() => onPieChartClick(row)}
            >
              <span>{`$${abbreviateNumber(row.totalHolisticRevenue || null, 1)}`}</span>
              {indicatorText && <div className={indicatorClassName}>{indicatorText}</div>}
            </InteractiveDiv>
          );
        }
      },
      {
        ...baseHeaderProps,
        name: 'totalHolisticRoas',
        label: 'ROAS',
        flex: 2,
        renderer: (row: PinnedSpendPointData) => {
          const isBau = row.totalBudget === businessAsUsual?.totalBudget;
          const { indicatorClassName, indicatorText } = getPercentageDifferenceIndicator(
            row.totalHolisticRoas || 0,
            businessAsUsual?.totalHolisticRoas || 0,
            isBau
          );
          return (
            <InteractiveDiv
              className={cn('ef-table-cell', 'ef-roas-cell', { 'business-as-usual': isBau })}
              onClick={() => onPieChartClick(row)}
            >
              <span>{formatNumber(row.totalHolisticRoas || null, 2)}</span>
              {indicatorText && <div className={indicatorClassName}>{indicatorText}</div>}
            </InteractiveDiv>
          );
        }
      }
    ];

    series.forEach(item => {
      headers.push({
        ...baseHeaderProps,
        name: `revenue:${item.id}`,
        label: `${item.label} Revenue`,
        flex: 2,
        renderer: (row: PinnedSpendPointData) => {
          const isBau = row.totalBudget === businessAsUsual?.totalBudget;
          return (
            <InteractiveDiv
              className={cn('ef-table-cell', { 'business-as-usual': isBau })}
              onClick={() => onPieChartClick(row)}
            >
              {`$${abbreviateNumber(row.revenueBySlot?.[item.id] || null, 1)}`}
            </InteractiveDiv>
          );
        }
      });
    });

    headers.push({
      nonInteractive: true,
      width: 64,
      renderer: (row: PinnedSpendPointData) => {
        const isBau = row.totalBudget === businessAsUsual?.totalBudget;
        return (
          <InteractiveDiv
            className={cn('ef-table-cell', 'actions-cell', { 'business-as-usual': isBau })}
            onClick={() => onPieChartClick(row)}
          >
            <Button
              appearance={Appearance.EMPTY}
              size={Size.SM}
              onClick={e => {
                e.stopPropagation();
                onPieChartClick(row);
              }}
            >
              <FaChartPie className='pie-chart-icon' />
            </Button>
            {!isBau && (
              <Button
                appearance={Appearance.EMPTY}
                size={Size.SM}
                onClick={e => {
                  e.stopPropagation();
                  setPinnedSpendPoints(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(row.totalBudget);
                    return newMap;
                  });
                }}
              >
                <MdClose className='close-icon' />
              </Button>
            )}
          </InteractiveDiv>
        );
      }
    });

    return headers;
  }, [businessAsUsual, onPieChartClick, setPinnedSpendPoints, series]);

  return (
    <div className='ef-pinned-chart-container'>
      <BPMTable
        data={pinnedSpendPointsData}
        headers={tableHeaders}
        filterBar={false}
        alternateColors={false}
        rowHoverClass='ef-row-hover'
        noRowsRenderer={renderNoRows}
      />
    </div>
  );
};
